import React, { memo, useCallback, useMemo } from "react";
import { Select, SingleValue } from "chakra-react-select";
import { TagCategoryFragment } from "gql/graphql";
import { useTags } from "modules/providers/tags-provider";
import colors from "app/colors";
import { SystemStyleObject } from "@chakra-ui/react";

interface IProps {
  isDisabled?: boolean;
  defaultTagCategoryId?: string;
  onChange: (value: TagCategoryFragment | null) => void;
  excludeTagCategoryIds?: string[];
}

const AdminTagCategorySelect = ({
  isDisabled = false,
  defaultTagCategoryId,
  onChange,
  excludeTagCategoryIds,
}: IProps) => {
  const { tagCategories, tagCategoriesById } = useTags();

  const options = useMemo(
    () =>
      tagCategories.filter(
        (tagCategory) =>
          !(excludeTagCategoryIds ?? []).includes(tagCategory.id),
      ),
    [excludeTagCategoryIds, tagCategories],
  );

  const defaultValue = useMemo(() => {
    if (defaultTagCategoryId) {
      return tagCategoriesById[defaultTagCategoryId];
    }
    return null;
  }, [defaultTagCategoryId, tagCategoriesById]);

  const _onChange = useCallback(
    (option: SingleValue<TagCategoryFragment>) => {
      onChange(option);
    },
    [onChange],
  );

  const formatOptionLabel = useCallback(
    (option: TagCategoryFragment) => option.name,
    [],
  );

  const getOptionLabel = useCallback(
    (option: TagCategoryFragment) => option.name,
    [],
  );

  const getOptionValue = useCallback(
    (option: TagCategoryFragment) => option.id,
    [],
  );

  const isEmpty = options.length === 0;

  return (
    <Select
      options={options}
      defaultValue={defaultValue}
      isDisabled={isDisabled || isEmpty}
      isClearable
      formatOptionLabel={formatOptionLabel}
      getOptionLabel={getOptionLabel}
      getOptionValue={getOptionValue}
      onChange={_onChange}
      components={{
        DropdownIndicator: () => null,
        IndicatorSeparator: () => null,
      }}
      chakraStyles={{
        placeholder: (provided: SystemStyleObject): SystemStyleObject => ({
          ...provided,
          fontStyle: "italic",
          color: colors.placeholderText,
        }),
      }}
      placeholder={
        isEmpty ? "No tag categories available" : "Select a tag category..."
      }
    />
  );
};

export default memo(AdminTagCategorySelect);
